import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { auth, db } from "./firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { ref, get, set, onValue, onDisconnect, remove } from "firebase/database";
import "./App.css";

import Home from "./components/Home";
import Login from "./components/Login";
import Register from "./components/Register";
import Chat from "./components/MainLayout";
import PhoneVerify from "./components/PhoneVerify";
import Version from "./components/Version";
import Privacy from "./components/Privacy";
import Terms from "./components/Terms";
import Docs from "./components/Docs";
import AIChatWindow from "./components/AIChatWindow";
import ModelExplorer from "./components/ModelExplorer";
import Pricing from "./components/Pricing";
import AdminPanel from "./components/AdminPanel";
import Support from "./components/Support";
import LandingTest from "./components/LandingTest";
import AuthAction from "./pages/AuthAction";

function App() {
  const [user, setUser] = useState(null);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [loading, setLoading] = useState(true);

  // 🌙 Theme system
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");

    if (savedTheme) {
      document.documentElement.setAttribute("data-theme", savedTheme);
      document.body.className = savedTheme; // Keeping fallback for legacy styling if any
    } else {
      const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      const defaultTheme = prefersDark ? "dark" : "light";
      document.documentElement.setAttribute("data-theme", defaultTheme);
      document.body.className = defaultTheme;
    }
  }, []);

  // 🔐 Auth + Verification Check
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        setUser(null);
        setLoading(false);
        return;
      }


      const emailVerified = currentUser.emailVerified;

      // Fetch phone verification from DB
      const snapshot = await get(ref(db, "users/" + currentUser.uid));
      const phoneVerified =
        snapshot.exists() && snapshot.val().isPhoneVerified;

      // ONE-TIME FEED: Promote the initial admin UID to RTDB
      if (currentUser.uid === "46F5cjVchVV2Wc3DIr6VIhGDQ5G2") {
        if (!snapshot.exists() || !snapshot.val().isAdmin) {
          await set(ref(db, `users/${currentUser.uid}/isAdmin`), true);
        }
      }

      setUser(currentUser);
      setIsEmailVerified(emailVerified);
      setIsPhoneVerified(phoneVerified);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // 🔄 Auto-poll for email verification if pending
  useEffect(() => {
    let interval;
    if (user && !isEmailVerified) {
      interval = setInterval(async () => {
        if (auth.currentUser) {
          await auth.currentUser.reload();
          if (auth.currentUser.emailVerified) {
            setIsEmailVerified(true);
            clearInterval(interval);
          }
        }
      }, 3000); // Check every 3 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [user, isEmailVerified]);

  // 🛡️ Global Session Limiter (Tab-Safe with SessionStorage)
  useEffect(() => {
    if (!user) return;
    
    // Reuse tabId across page reloads in the same browser tab
    let tabId = sessionStorage.getItem("bc_tab_id");
    if (!tabId) {
      tabId = Math.random().toString(36).substring(2, 15);
      sessionStorage.setItem("bc_tab_id", tabId);
    }

    const sessionRef = ref(db, `status/${user.uid}/sessions/${tabId}`);
    const allSessionsRef = ref(db, `status/${user.uid}/sessions`);
    
    // Heartbeat update
    set(sessionRef, { timestamp: Date.now() });
    onDisconnect(sessionRef).remove(); // Auto-remove on tab close

    const enforceLimits = async () => {
      const userSnap = await get(ref(db, `users/${user.uid}`));
      const isAdmin = userSnap.exists() && userSnap.val().isAdmin;
      const limit = isAdmin ? 10 : 5; // Generous multi-tab headroom

      const unsub = onValue(allSessionsRef, (snap) => {
        const sessions = snap.val() || {};
        const now = Date.now();

        // Filter out stale sessions older than 60 seconds
        const activeSessions = Object.entries(sessions).filter(
          ([_, val]) => val && val.timestamp && now - val.timestamp < 60000
        );

        const sessionKeys = activeSessions.map(([k]) => k);

        // If there are too many active sessions, purge the oldest overflow
        if (sessionKeys.length > limit) {
          const sorted = activeSessions.sort((a, b) => a[1].timestamp - b[1].timestamp);
          const newestKey = sorted[sorted.length - 1][0];

          if (newestKey === tabId) {
            const oldestKey = sorted[0][0];
            remove(ref(db, `status/${user.uid}/sessions/${oldestKey}`));
          }
        }
      });

      return unsub;
    };

    let unsubListener;
    enforceLimits().then((unsub) => { unsubListener = unsub; });

    return () => {
      if (unsubListener) unsubListener();
    };
  }, [user]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loader"></div>
        <div className="loading-text">Loading Bharat Connect...</div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/test" element={<LandingTest />} />
        
        <Route path="/version" element={<Version />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/docs" element={<Docs />} />
        <Route path="/ai-models" element={<ModelExplorer />} />
        <Route path="/auth/action" element={<AuthAction />} />

        <Route
          path="/login"
          element={user && isEmailVerified ? <Navigate to="/chat" /> : <Login />}
        />

        <Route
          path="/register"
          element={user && isEmailVerified ? <Navigate to="/chat" /> : <Register />}
        />

        {/* 🔐 Phone Verification Route Temporarily Disabled 
        <Route
          path="/phone-verify"
          element={
            user && isEmailVerified && !isPhoneVerified ? (
              <PhoneVerify />
            ) : (
              <Navigate to="/chat" />
            )
          }
        />
        */}

        {/* 🔐 Protected Chat Route */}
        <Route
          path="/chat"
          element={
            user ? (
              isEmailVerified ? (
                /* isPhoneVerified is Temporarily Bypassed */
                <Chat currentUser={user} />
              ) : (
                <Navigate to="/login" />
              )
            ) : (
              <Navigate to="/login" />
            )
          }
        />

        {/* 🤖 Sampark AI Assistant Route */}
        <Route
          path="/SamparkAI"
          element={user && isEmailVerified ? <AIChatWindow currentUser={user} /> : <Navigate to="/login" />}
        />

        {/* 💳 Pricing Route */}
        <Route path="/pricing" element={<Pricing />} />

        {/* 🛡️ Admin Route */}
        <Route path="/admin" element={user ? <AdminPanel currentUser={user} /> : <Navigate to="/login" />} />

        {/* 🆘 Support Route */}
        <Route path="/support" element={<Support currentUser={user} />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
